<!--@format-->
<template>
    <div class="upload-solution-csv-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Upload Solutions</h3>
                <div class="svg-container widget-close-button" @click="closeWidget()">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-svg v-if="loading" />
                <div v-else>
                    <div class="intro-container">
                        <h4>Upload Solutions via a CSV file</h4>
                        <p>
                            Before you upload your files below, make sure your file is ready to be
                            imported.
                        </p>
                    </div>
                    <div class="section">
                        <div class="section-container">
                            <h4>Download sample files</h4>
                            <p>
                                Start with a sample file to help ensure a smooth import. Add your
                                data in the correct columns and upload.
                            </p>
                            <app-button
                                class="download-sample-btn"
                                @click="handleDownloadSampleCsv"
                            >
                                Download Sample
                                <list-download-svg />
                            </app-button>
                        </div>
                    </div>
                    <div class="section">
                        <div class="section-container">
                            <app-drag-and-drop-files
                                library-enabled
                                :disabled="isDragAndDropDisabled"
                                :file-accept="csvFileConfig.fileSelectorAllowedTypes"
                                :files="csvFiles"
                                label="Or Drag File Here"
                                :loading-files="loadingFiles"
                                :library-file-types="csvFileConfig.libraryTypes"
                                @upload="handleDocumentUpload"
                                @library:selected="handleDocumentSelected"
                                @remove="deleteDocumentFile"
                            />
                            <div class="requirements">
                                <label>
                                    Maximum upload file size:
                                    <span> 100 MB</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget()"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button class="save-btn" @click="uploadCsv()"> Upload </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppDragAndDropFiles = () => import('@/components/common/app-drag-and-drop-files.vue');
const ListDownloadSvg = () => import('@/components/svg/list-download-svg');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
import { LogLevel } from '@/scripts/logger';

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'upload-solution-csv-widget',

    components: {
        'app-button': AppButton,
        'app-drag-and-drop-files': AppDragAndDropFiles,
        'list-download-svg': ListDownloadSvg,
        'loading-svg': LoadingSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            loadingFiles: false,
            csvFiles: [],
            csvFile: null,
            actionUrl: 'https://cdn.honeycrm.com/files/solutions-sample.csv',

            csvFileConfig: {
                byteSizeLimit: 104857600,
                byteSizeMinimum: 0,
                fileSelectorAllowedTypes: ['csv'],
                allowedFileTypes: ['text/csv'],
                libraryTypes: [
                    'Excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ],
            },
        };
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        isDragAndDropDisabled() {
            return this.loadingFiles || this.csvFiles.length > 0;
        },
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleDownloadSampleCsv() {
            window.open(this.actionUrl, '_blank');
        },

        deleteDocumentFile({ id }) {
            this.csvFiles = this.csvFiles.filter((file) => file.id !== id);
            this.csvFile = null;
        },

        handleDocumentSelected(event) {
            this.csvFiles = this.csvFiles.concat(
                event.map((item) => ({
                    id: item.id,
                    name: item.previewName,
                    size: item.size,
                    type: item.type,
                    url: item.url,
                })),
            );
        },

        getFilesFromSource(sourceEvent) {
            if (sourceEvent.currentTarget.files) {
                return sourceEvent.currentTarget.files;
            } else if (sourceEvent.dataTransfer) {
                return sourceEvent.dataTransfer.files;
            } else if (sourceEvent) {
                if (sourceEvent.dataTransfer) {
                    return sourceEvent.dataTransfer.files;
                } else if (sourceEvent.target.files) {
                    return sourceEvent.target.files;
                }
            }
            return null;
        },

        handleDocumentUpload(e) {
            let files = this.getFilesFromSource(e);
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                try {
                    if (file.size < this.csvFileConfig.byteSizeMinimum) {
                        throw new Error('Low Size');
                    }
                    if (file.size > this.csvFileConfig.byteSizeLimit) {
                        throw new Error('Over Size');
                    }
                    this.csvFile = file;
                    this.csvFiles.push({
                        id: this.$helpers.createId('doc'),
                        name: file.name,
                        size: file.size,
                        type: file.type,
                    });
                } catch (error) {
                    if (error.message === 'Over Size') {
                        this.$helpers.showFailNotification('File size exceeds the maximum amount', {
                            level: LogLevel.INFO,
                        });
                    } else if (error.message === 'Low Size') {
                        this.$helpers.showFailNotification('Low file size', {
                            level: LogLevel.INFO,
                        });
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred while uploading your document. Please check your format and try again.',
                            { error },
                        );
                    }
                }
            }
        },

        uploadCsv() {
            const formData = new FormData();

            formData.append('file', this.csvFile, this.csvFile.name);
            formData.append('accountId', this.accountId);

            let self = this;
            self.$api
                .ajax({
                    url: '/api/Solutions/UploadCSV',
                    method: 'POST',
                    data: formData,
                    contentType: false,
                    processData: false,
                }).then(() => {
                    this.$helpers.showSuccessNotification(
                        'We received your upload. It may take a few moments before they appear.',
                    );
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while uploading your document. Please try again.',
                        { error },
                    );
                })
                .finally(() => {
                    EventBus.$emit('solution:update');
                    this.closeWidget();
                });
        },

    },
};
</script>

<style>
.upload-solution-csv-widget .loading-screen {
    width: 100%;
    text-align: center;
}

.upload-solution-csv-widget .intro-container {
    margin-bottom: 30px;
}

.upload-solution-csv-widget .content-wrapper {
    height: 100%;
}

.upload-solution-csv-widget .content-wrapper .section {
    margin-bottom: 24px;
}

.upload-solution-csv-widget .download-sample-btn {
    color: #ffffff;
    background: #18aeff;
    font-size: 12px;
}

.upload-solution-csv-widget .download-sample-btn svg {
    height: 18px;
    margin-left: 16px;
}

.upload-solution-csv-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.upload-solution-csv-widget .widget-footer .action-container {
    display: flex;
}

.upload-solution-csv-widget .widget-footer .action-container .save-btn {
    margin-left: 15px;
}

.upload-solution-csv-widget .section-container .requirements label {
    font: 400 16px/24px 'Roboto';
    color: #877d88;
    margin: 0;
    margin-top: 16px;
}

.upload-solution-csv-widget .section-container .requirements span {
    font-weight: 500;
}

.upload-solution-csv-widget
    .section-container
    .app-drag-and-drop-files
    .app-drag-and-drop-files-uploader {
    padding: 28px;
}

.upload-solution-csv-widget
    .section-container
    .app-drag-and-drop-files 
    .files-preview-container
    .files-title {
    display: none;
}
</style>
